import { useCallback } from 'react';

/**
 * Hook for managing anonymous chat persistence
 *
 * Web (iframe): Uses sessionStorage (clears on tab close)
 * React Native (WebView): Uses localStorage (persists across app restarts)
 *
 * Note: contextKey from generateContextKey() is already fully hashed
 */

const STORAGE_PREFIX = '_sw_cid';

const isReactNative = (): boolean => {
  return !!(window as Window & { ReactNativeWebView?: unknown }).ReactNativeWebView;
};

export const useAnonymousChat = () => {
  const getStoredChatId = useCallback((contextKey: string): string | null => {
    try {
      if (!contextKey) {
        console.error('[useAnonymousChat] contextKey is required but was empty');
        return null;
      }
      const key = `${STORAGE_PREFIX}_${contextKey}`;
      if (isReactNative()) {
        return localStorage.getItem(key);
      }
      return sessionStorage.getItem(key);
    } catch (error) {
      console.warn('Failed to access storage:', error);
      return null;
    }
  }, []);

  const setStoredChatId = useCallback((chatId: string, contextKey: string): void => {
    try {
      if (!contextKey) {
        console.error('[useAnonymousChat] contextKey is required but was empty - skipping storage');
        return;
      }
      const key = `${STORAGE_PREFIX}_${contextKey}`;
      if (isReactNative()) {
        localStorage.setItem(key, chatId);
      } else {
        sessionStorage.setItem(key, chatId);
      }
    } catch (error) {
      console.warn('Failed to set storage:', error);
    }
  }, []);

  const clearStoredChatId = useCallback((contextKey: string): void => {
    try {
      if (!contextKey) {
        console.error('[useAnonymousChat] contextKey is required but was empty - skipping clear');
        return;
      }
      const storage = isReactNative() ? localStorage : sessionStorage;
      storage.removeItem(`${STORAGE_PREFIX}_${contextKey}`);
    } catch (error) {
      console.warn('Failed to clear storage:', error);
    }
  }, []);

  return { getStoredChatId, setStoredChatId, clearStoredChatId };
};
